import {
  BISHOP,
  KNIGHT,
  QUEEN,
  ROOK,
  WHITE,
  describeStatus,
  fileOf,
  isGameOver,
  rankOf,
  winnerOf,
  type Color,
  type GameStatus,
  type PromotionPiece,
  type Reaction,
  type Square,
} from '@chess/core'
import { useEffect, useState, type JSX, type ReactNode } from 'react'
import { PieceGlyph } from '../board/pieces.js'
import type { ConnectionStatus } from './connection.js'

const CHOICES: readonly PromotionPiece[] = [QUEEN, KNIGHT, ROOK, BISHOP]

export interface PromotionPickerProps {
  readonly to: Square
  readonly color: Color
  readonly orientation: Color
  readonly onChoose: (piece: PromotionPiece) => void
  readonly onCancel: () => void
}

/**
 * A column of four pieces dropping from the promotion square, as chess.com
 * does it — the choice appears where you are already looking, rather than in a
 * modal in the middle of the screen.
 */
export const PromotionPicker = ({
  to,
  color,
  orientation,
  onChoose,
  onCancel,
}: PromotionPickerProps): JSX.Element => {
  const column = orientation === WHITE ? fileOf(to) : 7 - fileOf(to)
  const fromTop = orientation === WHITE ? rankOf(to) === 7 : rankOf(to) === 0

  return (
    <div
      className="overlay-in absolute inset-0 z-40 bg-black/45"
      onPointerDown={onCancel}
      onContextMenu={(event) => {
        event.preventDefault()
        onCancel()
      }}
      role="dialog"
      aria-label="Choose a promotion piece"
    >
      <div
        className="promotion-in absolute flex flex-col overflow-hidden rounded shadow-2xl"
        style={{
          left: `${column * 12.5}%`,
          width: '12.5%',
          // Grow away from the promotion square rather than from nowhere.
          ['--promotion-origin' as string]: fromTop ? 'top' : 'bottom',
          ...(fromTop ? { top: 0 } : { bottom: 0, flexDirection: 'column-reverse' }),
        }}
      >
        {CHOICES.map((piece) => (
          <button
            key={piece}
            type="button"
            onPointerDown={(event) => {
              event.stopPropagation()
              onChoose(piece)
            }}
            className="aspect-square w-full bg-neutral-100 transition-colors hover:bg-accent-strong"
          >
            <PieceGlyph color={color} type={piece} className="h-full w-full" />
          </button>
        ))}
      </div>
    </div>
  )
}

export interface GameOverCardProps {
  readonly status: GameStatus
  readonly myColor: Color | null
  readonly names: readonly [string, string]
  readonly rematchPending: boolean
  /** You have already offered and are waiting on the other seat. */
  readonly awaitingOpponent: boolean
  readonly canRematch: boolean
  readonly onRematch: () => void
  readonly onDismiss: () => void
}

export const GameOverCard = ({
  status,
  myColor,
  names,
  rematchPending,
  awaitingOpponent,
  canRematch,
  onRematch,
  onDismiss,
}: GameOverCardProps): JSX.Element | null => {
  if (!isGameOver(status)) return null

  const winner = winnerOf(status)
  const headline =
    winner === null
      ? 'Draw'
      : myColor === null
        ? `${winner === WHITE ? names[0] : names[1]} won`
        : winner === myColor
          ? 'You won'
          : 'You lost'

  return (
    <div className="overlay-in absolute inset-0 z-30 flex items-center justify-center bg-black/45 p-4">
      <div className="card-in w-full max-w-xs rounded-panel bg-panel p-5 text-center shadow-2xl">
        <h2 className="text-2xl font-bold">{headline}</h2>
        <p className="mt-1 text-sm text-ink-dim first-letter:uppercase">{describeStatus(status)}</p>

        <button
          type="button"
          disabled={!canRematch || awaitingOpponent}
          onClick={onRematch}
          className="mt-4 w-full rounded bg-accent px-4 py-2.5 font-bold text-black hover:bg-accent-strong disabled:opacity-40"
        >
          {awaitingOpponent
            ? 'Rematch offered — waiting…'
            : rematchPending
              ? 'Accept rematch'
              : 'Rematch'}
        </button>
        <button
          type="button"
          onClick={onDismiss}
          className="mt-2 w-full rounded px-4 py-2 text-sm font-semibold text-ink-dim hover:text-ink"
        >
          Review the board
        </button>
      </div>
    </div>
  )
}

/**
 * Reactions from the last couple of seconds, floating up over the board.
 *
 * Self-expiring: it schedules a single timeout for the oldest live reaction
 * rather than being driven by a clock ticking in the parent, which used to
 * re-render the entire board ten times a second.
 */
export const FloatingReactions = ({
  reactions,
}: {
  readonly reactions: readonly Reaction[]
}): JSX.Element => {
  const [, sweep] = useState(0)

  useEffect(() => {
    if (!reactions.some((reaction) => Date.now() - reaction.at < 2_000)) return
    const id = window.setTimeout(() => sweep((value) => value + 1), 2_100)
    return () => window.clearTimeout(id)
  }, [reactions])

  return (
    <div className="pointer-events-none absolute inset-0 z-20 overflow-hidden">
      {reactions
        .filter((reaction) => Date.now() - reaction.at < 2_000)
        .map((reaction) => (
          <span
            key={`${reaction.at}-${reaction.slot}`}
            className="reaction-float absolute bottom-8 text-5xl drop-shadow-lg"
            style={{ left: reaction.slot === 0 ? '32%' : '68%' }}
          >
            {reaction.emoji}
          </span>
        ))}
    </div>
  )
}

/**
 * Connection trouble, floated over the board.
 *
 * A banner in the layout reflowed everything below it the moment the network
 * hiccuped, which moved the board under your finger. Also watches
 * `navigator.onLine`, because that flips instantly while a socket takes seconds
 * to notice it has died.
 */
export const ConnectionPill = ({ status }: { readonly status: ConnectionStatus }): JSX.Element | null => {
  const [online, setOnline] = useState(() => navigator.onLine)

  useEffect(() => {
    const update = (): void => setOnline(navigator.onLine)
    window.addEventListener('online', update)
    window.addEventListener('offline', update)
    return () => {
      window.removeEventListener('online', update)
      window.removeEventListener('offline', update)
    }
  }, [])

  if (online && status !== 'connecting') return null
  const label = online ? 'Reconnecting…' : 'Offline'

  return (
    <div
      role="status"
      className="pill-in pointer-events-none absolute top-3 left-1/2 z-30 -translate-x-1/2 rounded-full bg-black/80 px-3 py-1.5 text-xs font-semibold text-white shadow-lg backdrop-blur-sm"
    >
      {label}
    </div>
  )
}

export const Toast = ({ children }: { readonly children: ReactNode }): JSX.Element => (
  <div
    role="status"
    className="pill-in pointer-events-none absolute bottom-3 left-1/2 z-30 -translate-x-1/2 rounded-full bg-black/85 px-3 py-1.5 text-xs font-semibold text-white shadow-lg backdrop-blur-sm"
  >
    {children}
  </div>
)

export const Banner = ({
  children,
  tone = 'info',
}: {
  readonly children: ReactNode
  readonly tone?: 'info' | 'warn'
}): JSX.Element => (
  <div
    className={`rounded px-3 py-2 text-center text-sm font-semibold ${
      tone === 'warn' ? 'bg-danger/20 text-ink' : 'bg-panel-raised text-ink'
    }`}
  >
    {children}
  </div>
)
